import { useState, useEffect } from "react";
import styled, {keyframes } from "styled-components";
import { cancelOrder, completedOrder, engageOrder, getRequestData, vefiryOrderPhone } from "../Logic/index01";
import Spinner from "../Logic/Spinner";
import { T } from "./Util";
import { geocode, RequestType, setKey } from "react-geocode";
import { messageDisplay } from "./Util";
import { roundUpToDecimal } from "./Util";




/* ── animations ── */
const fadeUp = keyframes`from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); }`;
const pulse = keyframes`0%,100% { box-shadow: 0 0 0 0 rgba(37,99,235,0.4); } 50% { box-shadow: 0 0 0 10px rgba(37,99,235,0); }`;
const dash = keyframes`to { stroke-dashoffset: 0; }`;
const dotPop = keyframes`0% { transform: scale(0); opacity: 0; } 70% { transform: scale(1.3); } 100% { transform: scale(1); opacity: 1; }`;



const Shell = styled.div` 
  position:relative;
  width: 30%;
  height:90vh;
  left: 5%;
  transform: translateX(-35%);
  border-radius: 42px;
  overflow: hidden;
  box-shadow: 0 40px 100px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.07);
  background: #e8eaed;
  @media (max-width: 768px) {
  width: 90%;
  height:95vh;
  left: 0%;
  transform: translateX(0%);
  }
`;




/* ── map background ── */
const MapBg = styled.div`
  position: absolute; 
  inset: 0;
  background:
    linear-gradient(rgba(232,234,237,0.9), rgba(232,234,237,0.9)),
    repeating-linear-gradient(0deg, transparent, transparent 39px, #d1d5db 39px, #d1d5db 40px),
    repeating-linear-gradient(90deg, transparent, transparent 39px, #d1d5db 39px, #d1d5db 40px);
`;



const MapBlock = styled.div`
  position: absolute;
  background: ${({ color }) => color || "#d4d8dc"};
  border-radius: ${({ r }) => r || "6px"};
  top: ${({ top }) => top};
  left: ${({ left }) => left};
  right: ${({ right }) => right};
  width: ${({ w }) => w};
  height: ${({ h }) => h};
  opacity: ${({ op }) => op || 1};
`;



/* ── SVG route ── */
const RouteSvg = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
`;



const RoutePathAnimated = styled.path`
  stroke: #2563eb;
  stroke-width: 4;
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 600;
  stroke-dashoffset: 600;
  animation: ${dash} 1.6s cubic-bezier(0.4,0,0.2,1) 0.3s forwards;
`;




/* ── top card (route info) ── */
const TopCard = styled.div`
  position: absolute;
  top: 18px; 
  left: 16px; 
  right: 16px;
  background: #fff;
  border-radius: 18px;
  padding: 14px 16px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.12);
  animation: ${fadeUp} 0.5s ease both;
`;



const RouteRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  &+& { margin-top: 10px; }
`;



const DotLine = styled.div`
  display: flex; 
  flex-direction: column; 
  align-items: center; 
  gap: 3px;
`;



const Dot = styled.div`
  width: 10px; 
  height: 10px; 
  border-radius: 50%;

  background: ${({ color }) => color};
  animation: ${dotPop} 0.5s ease ${({ delay }) => delay || "0s"} both;
`;



const Line = styled.div`
  width: 2px; height: 22px; background: #cbd5e1; border-radius: 2px;
`;

const RouteLabel = styled.div`
  font-family: 'DM Sans', sans-serif;
`;

const RouteTitle = styled.div`
  font-size: 12px; font-weight: 600; color: #1e293b;
`;

const RouteSub = styled.div`
  font-size: 11px; color: #94a3b8; margin-top: 1px;
`;

/* ── driver card ── */
const DriverCard = styled.div`
  position: absolute;
  top: 168px; right: 20px;
  background: #fff;
  border-radius: 16px;
  padding: 10px 14px 10px 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 6px 28px rgba(0,0,0,0.14);
  animation: ${fadeUp} 0.5s ease 0.15s both;
`;


const Avatar = styled.div`
  width: 42px; height: 42px; border-radius: 12px; overflow: hidden;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  display: flex; align-items: center; justify-content: center;
  font-size: 18px; color: #fff; font-weight: 700;
  flex-shrink: 0;
`;

const DriverInfo = styled.div``;
const DriverName = styled.div`font-size: 13px; font-weight: 700; color: #1e293b;`;
const DriverRole = styled.div`font-size: 11px; color: #64748b; margin-top: 1px;`;
const PlateTag = styled.span`
  display: inline-block; margin-top: 4px;
  background: #f1f5f9; border-radius: 6px;
  padding: 2px; 
  font-size: 10px; 
  font-weight: 600; 
  color: #475569;
  letter-spacing: 0.5px;
`;



const PickupDot = styled.div`
  position: absolute;
  top: 248px; right: 72px;
  width: 14px; height: 14px; border-radius: 50%;
  background: #2563eb;
  border: 3px solid #fff;
  box-shadow: 0 0 0 3px rgba(37,99,235,0.25);
  animation: ${pulse} 2s infinite;
`;

/* ── destination badge ── */
const DestBadge = styled.div`
  position: absolute;
  bottom: 123px; margin: 10px;
  display: flex; align-items: center; gap: 0; 
  animation: ${fadeUp} 0.5s ease 0.35s both;
`;

const DestDot = styled.div`
  position: absolute;
  bottom: 195px; left: 69px;
  width: 14px; height: 14px; border-radius: 50%;
  background: #fff;
  border: 3px solid #2563eb;
  box-shadow: 0 2px 8px rgba(37,99,235,0.3);
`;

/* ── confirm button ── */
const ConfirmBtn = styled.button`
  position: absolute;
  bottom: 68px; left: 20px; right: 20px;
  height: 58px;
  background:linear-gradient(135deg,  #2563eb 0%, #1d4ed8 100%);
  border: none; border-radius: 18px;
  color: #fff;
  font-family: 'Syne', sans-serif;
  font-size: 12px; 
  font-weight: 800;
  letter-spacing: 0.5px;
  cursor: pointer;
  box-shadow: 0 8px 28px rgba(37,99,235,0.45);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  animation: ${fadeUp} 0.5s ease 0.45s both;
  &:hover { transform: translateY(-2px); box-shadow: 0 12px 36px rgba(37,99,235,0.55); }
  &:active { transform: translateY(0); box-shadow: 0 4px 16px rgba(37,99,235,0.35); }
`;


const CancelBtn = styled(ConfirmBtn)`
bottom:16px;
height:40px;
background:#ff0000;
font-size: 12px; 
`;


/* ── location pin ── */
const CompassBtn = styled.div`
  position: absolute;
  bottom: 230px; right: 20px;
  width: 36px; height: 36px;
  background: #fff;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 2px 12px rgba(0,0,0,0.15);
  font-size: 16px;
`;



const Request = ({clicker}) => {


  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orderpayload, setOrderpayload] = useState({});
  const [currentAdd, setcurrentAdd] = useState("");
  const [btnLabel, setBtnLabel] = useState("Confirm");




   const getData = async (data) => {
      getRequestData(data)
        .then((res) => {
     
        setLoading(true)
          if(res !== undefined && res != "No value present"){
            setLoading(false);
      
            
            if(res.Status){
              setBtnLabel(`Complete order  ₦${(roundUpToDecimal(res.amount,2))}`)
            } 
      
            if(res.elatitude != 0 && res.elongitude != 0){
               getAddressFromCoords(res.elatitude,res.elongitude)
                .then((position) => {
                 setOrderpayload({"dist":position,
                                 "elatitude":res.elatitude,
                                  "elongitude":res.elongitude,
                                  "cartype":res.carType,
                                  "requestId":res.requestId,
                                  "accesscode":res.accesscode,
                                  "sessionId":res.sessionId,
                                  "status":res.Status
                      });
               });
              }else{
                  setOrderpayload({"dist":"BARGAIN",
                                   "elatitude":"0",
                                    "elongitude":"0",
                                    "cartype":res.carType,
                                    "requestId":res.requestId,
                                    "accesscode":res.accesscode,
                                    "sessionId":res.sessionId,
                                    "status":res.Status
                        });
              }
             
              
          }else{
            messageDisplay("Order already taken!",1);
        }      
      })
    }



  async function engageOrderState(data){       
        if(btnLabel == "Confirm"){
         setLoading(true);
         let res = await engageOrder(data);
         updateUi(res);
       }else  
          if(btnLabel == "Contact vehicle owner"){
           let res = await vefiryOrderPhone(data)
           await navigator.clipboard.writeText(res);
           messageDisplay("Phone number copied",0);
       }else{
         if(data.status){
          setLoading(true);
          let res = await completedOrder(data);
          updateUi(res);
       }
    }
  }
  


  
function updateUi(res){
    setConfirmed(true);
      if(res !== undefined ){
        setLoading(false);
          messageDisplay(res, res.includes("Proceed") | res.includes("Completed.") ? 0 : 1);
            setBtnLabel("✓ Confirmed!");
              setTimeout(() => {
                setConfirmed(false)
                setBtnLabel("Contact vehicle owner");
              }, 2500); 
        }      
}



setKey(process.env.REACT_APP_GOOGLE_API_KEY);
const getAddressFromCoords = async (lat, lng) => {
  try{
      let geo = await geocode(RequestType.LATLNG, `${lat},${lng}`);
        return geo.results[0].formatted_address;
    }catch(e){
      return e;
  }    
};




useEffect(() => {
  getData(window.location.href.toString().substring(window.location.href.indexOf("console")));
     if(navigator.geolocation){
        navigator.geolocation.getCurrentPosition(async (position) => {          
          setcurrentAdd(await getAddressFromCoords(position.coords.latitude,position.coords.longitude))
        },(error) => {
          messageDisplay(error.message,1)          
        });
      }
  },[]);




  return (<>
            {loading ?
               <div className="flex justify-center items-center  w-full z-50"> 
                  <Spinner  color={T.blue}  isloading={loading}/>
                </div>  
                :
              <Shell>
                {/* MAP */}
                <MapBg />
                <MapBlock color="#c8cdd3" top="80px" left="0" w="120px" h="90px" r="0 12px 12px 0" />
                <MapBlock color="#d6dde3" top="170px" left="10px" w="80px" h="60px" />
                <MapBlock color="#c2c9d0" top="240px" left="0" w="60px" h="100px" r="0 8px 8px 0" />
                <MapBlock color="#dde2e7" top="330px" left="20px" w="100px" h="80px" />
                <MapBlock color="#cdd4db" top="460px" left="0" w="140px" h="70px" r="0 10px 10px 0" />
                <MapBlock color="#bfc8cf" top="100px" right="0" w="90px" h="120px" r="12px 0 0 12px" />
                <MapBlock color="#d0d8df" top="260px" right="10px" w="70px" h="90px" />
                <MapBlock color="#c9d1d8" top="380px" right="0" w="110px" h="80px" r="12px 0 0 12px" />
                <MapBlock color="#b8c4cc" top="480px" right="20px" w="80px" h="60px" />

                

                {/* ROUTE SVG */}
                <RouteSvg viewBox="0 0 375 720" preserveAspectRatio="none">
                  <RoutePathAnimated d="M 80 580 L 80 440 L 290 440 L 290 270" />
                </RouteSvg>

                {/* TOP CARD */}
                <TopCard>
                  {/* <div class="relative"><div class="absolute right-0 top-0 text-3xl"  onClick={clicker}>X</div></div>       */}
                  <RouteRow>
                    <DotLine>
                      <Dot color="#2563eb" delay="0.1s" />
                      <Line />
                      <Dot color="#1e293b" delay="0.2s" />
                    </DotLine>
                    <RouteLabel>
                      <RouteTitle>Current location</RouteTitle>
                      <RouteSub>{currentAdd}</RouteSub>
                      <div style={{ height: 14 }} />
                      <RouteTitle>Destination</RouteTitle>
                      <RouteSub>{orderpayload.dist}</RouteSub>
                    </RouteLabel>
                  </RouteRow>
                </TopCard>
                {/* DRIVER CARD */}
                <DriverCard>
                       <Avatar><span class="material-symbols-outlined">
                            car_crash
                            </span>
                        </Avatar>
                  <DriverInfo>
                    <DriverName>#{orderpayload.requestId}</DriverName>
                    <DriverRole>{orderpayload.accesscode}</DriverRole>
                    <PlateTag>{orderpayload.cartype}</PlateTag>
                  </DriverInfo>
                </DriverCard>
                {/* PICKUP */}
                <PickupDot />

                
                {/* DESTINATION */}
                <DestDot />
                <DestBadge className="flex-col">
                  <div className="bg-stone-50 rounded-lg p-2 m-1 h-auto flex flex-row">
                    <Avatar> 
                      <span class="material-symbols-outlined">
                          handyman
                        </span>
                    </Avatar>
                    <DriverInfo>
                      <PlateTag>{orderpayload.dist}</PlateTag>
                    </DriverInfo>
                  </div>

                </DestBadge>

                <CompassBtn>⊕</CompassBtn>
                 <ConfirmBtn color="" onClick={(e) => engageOrderState(orderpayload)} disabled={confirmed}>
                   {loading ?  <Spinner   color={T.surface}  isloading={loading}/> : btnLabel}
                 </ConfirmBtn>
      
                  <CancelBtn className="w-3/5" onClick={(e) => {
                    cancelOrder(orderpayload);
                    setLoading(false);
                    clicker()
                  }} disabled={confirmed}>
                  {"Cancel"}
                </CancelBtn>
                <CancelBtn className="w-1/4 ml-auto" onClick={(e) => clicker()}>
                  {"X"}
                </CancelBtn>
               
              </Shell>
      }
        
    </>
  );
}

export default Request;